<template>
  <section class="configure-company-phase-rules">
    <div class="header">
      <h2>Regras de horas extras da empresa (fases)</h2>
      <p>
        Estas são as regras <strong>padrão da empresa</strong> (CompanySettings).
        Valem para todos os turnos que seguem a configuração da empresa
        (<em>"usar mesma configuração da empresa"</em>). Ajuste as fases por tipo de
        dia e os percentuais aplicados ao saldo.
      </p>
    </div>

    <div v-if="loading" class="loading-block">Carregando configurações da empresa...</div>

    <div v-if="!loading">
      <!-- Percentuais das fases (nível da empresa) -->
      <div class="box">
        <label class="box-title">Percentuais das fases de hora extra (da empresa)</label>
        <p class="hint-line">
          O percentual é único por fase (aplicado a todos os tipos de dia). Valores
          em número inteiro (ex.: 50 = +50%).
        </p>
        <div class="percent-row">
          <div class="percent-field">
            <label>% 1ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.firstPhasePercent" placeholder="Ex: 50" />
          </div>
          <div class="percent-field">
            <label>% 2ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.secondPhasePercent" placeholder="Ex: 60" />
          </div>
          <div class="percent-field">
            <label>% 3ª fase</label>
            <input type="number" min="0" step="1" v-model.number="form.thirdPhasePercent" placeholder="Ex: 100" />
          </div>
        </div>
      </div>

      <!-- Regras de fases -->
      <div class="box">
        <label class="box-title">Regras (uma linha por tipo de dia)</label>
        <p class="hint-line">
          Cada linha representa um tipo de dia. Ative só os dias que você quer que
          tenham fase extraordinária. Durações no formato HH:MM:SS.
        </p>
        <table class="table-rules">
          <thead>
            <tr>
              <th title="Dia da semana, feriado ou folga ao qual essa regra se aplica.">
                {{ labels.workshiftDayPhase.workshiftDayPhaseType }}
              </th>
              <th title="Quando desmarcado, o sistema trata esse dia como sem fase extraordinária ativa.">
                {{ labels.workshiftDayPhase.isActive }}
              </th>
              <th title="Tempo máximo que conta como 1ª fase de hora extra (HH:MM:SS). Após esse limite, a hora migra para a 2ª fase.">
                {{ labels.workshiftDayPhase.firstPhaseDuration }}
              </th>
              <th title="Marque se as horas da 1ª fase devem somar no saldo do banco de horas.">
                {{ labels.workshiftDayPhase.applyFirstPercentageToBalance }}
              </th>
              <th title="Tempo máximo que conta como 2ª fase (após a 1ª). Acima desse limite, a hora vira 3ª fase.">
                {{ labels.workshiftDayPhase.secondPhaseDuration }}
              </th>
              <th title="Marque se as horas da 2ª fase devem somar no saldo do banco de horas.">
                {{ labels.workshiftDayPhase.applySecondPercentageToBalance }}
              </th>
              <th title="Tempo da 3ª fase. 00:00:00 = sem limite.">
                {{ labels.workshiftDayPhase.thirdPhaseDuration }}
              </th>
              <th title="Marque se as horas da 3ª fase devem somar no saldo do banco de horas.">
                {{ labels.workshiftDayPhase.applyThirdPercentageToBalance }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.workshiftDayPhaseType">
              <td>{{ helpers.getPhaseTypeLabel(rule.workshiftDayPhaseType) }}</td>
              <td><input type="checkbox" v-model="rule.isActive" /></td>
              <td><input type="text" v-model="rule.firstPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applyFirstPercentageToBalance" /></td>
              <td><input type="text" v-model="rule.secondPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applySecondPercentageToBalance" /></td>
              <td><input type="text" v-model="rule.thirdPhaseDuration" placeholder="00:00:00" title="HH:MM:SS"/></td>
              <td><input type="checkbox" v-model="rule.applyThirdPercentageToBalance" /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="actions">
        <button class="btn-cancel" @click="$router.go(-1)">Voltar</button>
        <button class="btn-primary" :disabled="saving" @click="save()">
          <span v-if="!saving">Salvar regras da empresa</span>
          <span v-else>Salvando...</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import {
  COMPANY_SETTINGS_PHASES,
  UPDATE_COMPANY_SETTINGS_PHASES
} from "../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import {
  workShiftLabels,
  workshiftDayPhaseLabels,
  workshiftDayPhaseTypeLabels,
  labelHelpers
} from "../../../../labels/workshiftLabels";
import swal from "sweetalert";

// Converte string TimeSpan vinda do backend ("HH:MM:SS" ou "HH:MM:SS.fff")
// para o mesmo formato usado nos inputs.
function normalizeTimeSpan(value) {
  if (!value) return "00:00:00";
  if (typeof value !== "string") return String(value);
  const cleaned = value.split(".")[0];
  const parts = cleaned.split(":");
  while (parts.length < 3) parts.push("00");
  return parts.slice(0, 3).map((p) => p.padStart(2, "0")).join(":");
}

// Percentual vem do backend como FRAÇÃO (0.5 = 50%). Na UI mostramos inteiro.
function fractionToPercentInt(fraction) {
  const n = Number(fraction) || 0;
  return Math.round(n * 100);
}
function percentIntToFraction(percent) {
  const n = Number(percent) || 0;
  return n / 100;
}

export default {
  name: "configureCompanyPhaseRules",
  data() {
    return {
      loading: false,
      saving: false,
      companyId: "",
      context: "",
      rules: [],
      form: {
        firstPhasePercent: 0,
        secondPhasePercent: 0,
        thirdPhasePercent: 0
      },
      labels: {
        workShift: workShiftLabels,
        workshiftDayPhase: workshiftDayPhaseLabels,
        phaseType: workshiftDayPhaseTypeLabels
      },
      helpers: labelHelpers
    };
  },
  beforeMount() {
    this.companyId = this.$route.params.idCompany;
    this.context = this.$route.params.context;
    this.load();
  },
  methods: {
    load() {
      this.loading = true;

      REQUEST_SERVICE.requestContext(
        "GET",
        `${COMPANY_SETTINGS_PHASES}?companyId=${this.companyId}`,
        this.context
      ).then((response) => {
        const data = response.data || {};

        this.form.firstPhasePercent = fractionToPercentInt(data.firstPhasePercent);
        this.form.secondPhasePercent = fractionToPercentInt(data.secondPhasePercent);
        this.form.thirdPhasePercent = fractionToPercentInt(data.thirdPhasePercent);

        const list = Array.isArray(data.workshiftDayPhasesList) ? data.workshiftDayPhasesList : [];
        this.rules = list.map((p) => ({
          workshiftDayPhaseType: p.workshiftDayPhaseType,
          isActive: !!p.isActive,
          firstPhaseDuration: normalizeTimeSpan(p.firstPhaseDuration),
          secondPhaseDuration: normalizeTimeSpan(p.secondPhaseDuration),
          thirdPhaseDuration: normalizeTimeSpan(p.thirdPhaseDuration),
          applyFirstPercentageToBalance: !!p.applyFirstPercentageToBalance,
          applySecondPercentageToBalance: !!p.applySecondPercentageToBalance,
          applyThirdPercentageToBalance: !!p.applyThirdPercentageToBalance
        }));

        this.loading = false;
      }).catch(() => {
        this.loading = false;
        swal("Aviso", "Falha ao carregar as configurações da empresa.", "error");
      });
    },
    save() {
      if (this.saving) return;
      this.saving = true;

      const payload = {
        companyId: this.companyId,
        // percentuais no nível da empresa: enviados como FRAÇÃO (backend grava direto).
        firstPhasePercent: percentIntToFraction(this.form.firstPhasePercent),
        secondPhasePercent: percentIntToFraction(this.form.secondPhasePercent),
        thirdPhasePercent: percentIntToFraction(this.form.thirdPhasePercent),
        workshiftDayPhasesList: this.rules.map((r) => ({
          isActive: r.isActive,
          workshiftDayPhaseType: r.workshiftDayPhaseType,
          firstPhaseDuration: r.firstPhaseDuration,
          secondPhaseDuration: r.secondPhaseDuration,
          thirdPhaseDuration: r.thirdPhaseDuration,
          applyFirstPercentageToBalance: r.applyFirstPercentageToBalance,
          applySecondPercentageToBalance: r.applySecondPercentageToBalance,
          applyThirdPercentageToBalance: r.applyThirdPercentageToBalance
        }))
      };

      REQUEST_SERVICE.requestContext("PUT", UPDATE_COMPANY_SETTINGS_PHASES, this.context, payload)
        .then(() => {
          this.saving = false;
          swal("Sucesso", "Regras da empresa atualizadas. Os turnos que seguem a configuração da empresa serão atualizados.", "success");
        })
        .catch(() => {
          this.saving = false;
          swal("Aviso", "Falha ao salvar as regras da empresa. Verifique os campos e tente novamente.", "error");
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.configure-company-phase-rules {
  padding: 24px;

  .header {
    margin-bottom: 24px;
    h2 { font-weight: bold; margin-bottom: 8px; }
    p { color: #666; font-size: 13px; }
  }

  .loading-block { text-align: center; padding: 40px; color: #888; }

  .box {
    background: #fff;
    border-radius: 6px;
    padding: 18px;
    margin-bottom: 18px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);

    .box-title {
      display: block;
      font-weight: 600;
      margin-bottom: 12px;
    }
  }

  .hint-line { font-size: 12px; color: #888; margin: 0 0 12px 0; }

  .percent-row {
    display: flex; gap: 16px; flex-wrap: wrap; margin-top: 10px;
    .percent-field {
      display: flex; flex-direction: column; gap: 4px;
      label { font-weight: 500; font-size: 13px; }
      input {
        border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; width: 120px;
      }
    }
  }

  .table-rules {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      border-bottom: 1px solid #eee;
      padding: 8px 6px;
      text-align: left;
      vertical-align: middle;
    }
    th {
      color: #555; font-weight: 600; background: #fafafa;
      cursor: help;
    }

    input[type="text"] {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 4px 6px;
      width: 90px;
      font-family: monospace;
    }
  }

  .actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 20px;

    .btn-cancel, .btn-primary {
      border-radius: 4px;
      padding: 8px 24px;
      font-weight: 600;
      border: none;
      cursor: pointer;
    }
    .btn-cancel { background: #8b8787; color: #fff; }
    .btn-primary { background: #3598dc; color: #fff; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/companyDetail/configureCompanyPhaseRules.vue